import React, { useEffect, useMemo, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "ui/Carousel";
import EntityAvatar from "components/Entities/EntityAvatar";
import EntityCompactCard from "components/Entities/EntityCompactCard";
import type { QuizDetail } from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";
import type { QuizGuessInput } from "lib/api";
import { getQuizButtonClassName, mapQuizUserToEntityAvatarUser } from "./quizUtils";

interface QuizPart2GuessesProps {
  ui: QuizUiStrings;
  quiz: QuizDetail;
  onSubmit: (guesses: QuizGuessInput[]) => Promise<void>;
  isSaving: boolean;
}

/**
 * Carousel-based guessing round for each other active player.
 */
const QuizPart2Guesses: React.FC<QuizPart2GuessesProps> = ({
  ui,
  quiz,
  onSubmit,
  isSaving,
}) => {
  const targets = quiz.part2.targets;
  const issueOptions = quiz.part2.issue_options;
  const initialGuessMap = useMemo(() => {
    const map = new Map<number, number>();
    (quiz.viewer?.guesses || []).forEach((guess) => {
      if (guess.target_user_id && guess.guessed_issue_id) {
        map.set(guess.target_user_id, guess.guessed_issue_id);
      }
    });
    return map;
  }, [quiz.viewer?.guesses]);

  const [guessMap, setGuessMap] = useState<Map<number, number>>(initialGuessMap);

  useEffect(() => {
    setGuessMap(initialGuessMap);
  }, [initialGuessMap]);

  if (targets.length === 0) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {ui.part2GuessesTitle}
        </h1>
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.noTargetsTitle}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {ui.part2GuessesTitle}
        </h1>
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.part2GuessesDescription}</p>
      </div>

      <Carousel className="w-full">
        <CarouselContent>
          {targets.map((target) => {
            const targetUserId = target.user_id ?? 0;
            const selectedIssueId = guessMap.get(targetUserId) ?? null;
            return (
              <CarouselItem key={targetUserId}>
                <div className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
                  <div className="mb-5 space-y-4">
                    <EntityAvatar
                      entity={mapQuizUserToEntityAvatarUser(target.user)}
                      entityType="user"
                      entityId={target.user_id ?? undefined}
                      entityChip={true}
                      showLink={true}
                      isInputField={false}
                      className="min-w-0"
                    />
                    <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {ui.guessPromptGeneric}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.guessOptionsTitle}</p>
                    <div className="grid gap-2 md:grid-cols-2">
                      {issueOptions.map((issue) => (
                        <button
                          key={`${targetUserId}-${issue.id}`}
                          type="button"
                          onClick={() =>
                            setGuessMap((current) => {
                              const next = new Map(current);
                              if (issue.id) {
                                next.set(targetUserId, issue.id);
                              }
                              return next;
                            })
                          }
                          className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                            selectedIssueId === issue.id
                              ? "border-eoe-blue bg-eoe-blue/5 text-eoe-text dark:border-eoe-blue dark:bg-eoe-blue/10 dark:text-eoe-dark-text"
                              : "border-eoe-border-main bg-eoe-bg-hover text-eoe-text dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text"
                          }`}
                        >
                          <EntityCompactCard
                            entityId={issue.id ?? undefined}
                            entityType="issue"
                            showActions={false}
                            showMeta={true}
                            compactCard={true}
                            showVote={false}
                            showLink={false}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <div className="mt-4 flex items-center justify-between">
          <CarouselPrevious hideWhenDisabled="invisible" />
          <CarouselDots />
          <CarouselNext hideWhenDisabled="invisible" />
        </div>
      </Carousel>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() =>
            void onSubmit(
              targets
                .filter((target) => target.user_id && guessMap.has(target.user_id))
                .map((target) => ({
                  targetUserId: target.user_id as number,
                  guessedIssueId: guessMap.get(target.user_id as number) as number,
                }))
            )
          }
          disabled={isSaving || guessMap.size !== targets.length}
          className={getQuizButtonClassName({
            disabled: Boolean(isSaving || guessMap.size !== targets.length),
          })}
        >
          {ui.submitGuessesAction}
        </button>
      </div>
    </div>
  );
};

export default QuizPart2Guesses;
